import { PlannerConfigEntityWithOptionalFields } from '@/services/configurator/PlannerConfigTypes';

export type CollidingCategory = {
  // The conflicting category is the primary thing the form needs to highlight.
  rangeInclude: string;
  // We keep planner ids aggregated here because fixing one planner/category pair
  // does not guarantee the category is conflict-free across all planners yet.
  plannerIds: string[];
  // Locales are also aggregated so the warning can show the full collision scope
  // without exploding into one row per planner-locale combination.
  locales: string[];
};

const EN_XZ = 'en-XZ';

type Collision = {
  category: string;
  plannerId: string;
  locale: string;
};

/**
 * Finds what planner collided with the current planner on which category and locale. A collision is defined as:
 * - Two different planners share at least one category in their rangeInclude AND
 * - They both have at least one overlapping active locale (excluding the locale 'en-XZ')
 *
 * @param plannerId - The id of the planner being edited
 * @param plannerActiveCategories - The currently active categories for that planner
 * @param plannerActiveLocales - The currently active locales for that planner
 * @param allPlanners - All existing planners to check against
 * @returns Collisions grouped by category, showing the aggregated planner ids and locales for each blocked category
 */
export function findCollidingCategories(
  plannerId: string,
  plannerActiveCategories: string[],
  plannerActiveLocales: string[],
  allPlanners: PlannerConfigEntityWithOptionalFields[],
): CollidingCategory[] {
  if (!plannerActiveCategories.length || !plannerActiveLocales.length) {
    return [];
  }

  const targetLocales = new Set(plannerActiveLocales);
  const targetCategories = new Set(plannerActiveCategories);
  const otherPlanners = allPlanners.filter((p) => p.id !== plannerId);
  const collisions = otherPlanners.flatMap((planner) =>
    findCollisionsForPlanner(planner, targetLocales, targetCategories),
  );

  return groupByCategory(collisions);
}

function findCollisionsForPlanner(
  planner: PlannerConfigEntityWithOptionalFields,
  targetLocales: Set<string>,
  targetCategories: Set<string>,
): Collision[] {
  const overlappingLocales = getOverlappingLocales(planner, targetLocales);
  const overlappingCategories = getOverlappingCategories(
    planner,
    targetCategories,
  );

  return overlappingCategories.flatMap((category) =>
    overlappingLocales.map((locale) => ({
      category,
      plannerId: planner.id,
      locale,
    })),
  );
}

function getOverlappingLocales(
  planner: PlannerConfigEntityWithOptionalFields,
  targetLocales: Set<string>,
): string[] {
  return (planner.marketData ?? [])
    .filter((m) => m.isActive)
    .map((m) => m.locale)
    .filter((locale) => locale !== EN_XZ && targetLocales.has(locale));
}

function getOverlappingCategories(
  planner: PlannerConfigEntityWithOptionalFields,
  targetCategories: Set<string>,
): string[] {
  return (planner.rangeInclude ?? []).filter((cat) =>
    targetCategories.has(cat),
  );
}

function groupByCategory(collisions: Collision[]): CollidingCategory[] {
  const categories = uniqueCategories(collisions);

  return categories.map((category) => {
    const matching = collisions.filter((c) => c.category === category);
    return {
      rangeInclude: category,
      plannerIds: unique(matching.map((c) => c.plannerId)),
      locales: unique(matching.map((c) => c.locale)),
    };
  });
}

function uniqueCategories(collisions: Collision[]): string[] {
  return [...new Set(collisions.map((c) => c.category))];
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}
